import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import { Paper, Box, Typography } from "@mui/material";
import CourseTypeIcon from "../Icons/CourseTypeIcon";
import CourseCardOverline from "./CourseCardOverline";
import CourseCardPicture from "./CourseCardPicture";
import Tooltip from "../Tooltip/Tooltip";
import PrerequisiteTooltipContent from "../Tooltip/PrerequisiteTooltipContent";
import { courseTypes } from "../../constants";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minHeight: 450 // Fix for IE 11
  },
  buttons: {
    "& button": {
      width: "100%"
    }
  }
}));

const CourseCardGridType = ({
  courseType,
  typeName,
  buttons,
  hasNote,
  imageSrc,
  dueDate,
  prerequisites,
  title,
  flags,
  location,
  expansionPanel,
  activitiesSummary,
  reattemptMessage,
  courseDescription,
  manualName,
  number,
  duration,
  isSelfAssigned
}) => {
  const classes = useStyles();

  let courseTitle =
    courseType == courseTypes.policy ? title + " | " + number : title;

  if (prerequisites && prerequisites.length > 0) {
    courseTitle = (
      <Tooltip
        title={<PrerequisiteTooltipContent prerequisites={prerequisites} />}
        placement="right-end"
      >
        <Typography variant="subtitle1" color="textPrimary">
          {courseTitle}
        </Typography>
      </Tooltip>
    );
  } else {
    courseTitle = (
      <Typography variant="subtitle1" color="textPrimary">
        {courseTitle}
      </Typography>
    );
  }

  const courseCardTooltipContainer = (
    <Box display="flex" flexDirection="column" mx={3} mt={3} flex={1}>
      <Box display="flex" ml={-0.25} mb={0.75}>
        <Box mr={1}>
          <CourseTypeIcon type={typeName} size="small" />
        </Box>
        {!!typeName && <CourseCardOverline courseType={typeName} />}
      </Box>

      <Typography variant="h6" color="textPrimary">
        {courseTitle}
      </Typography>
      {courseType == courseTypes.policy && manualName && (
        <Box mt={0.5}>
          <Typography variant="body2" color="textSecondary">
            {manualName}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Paper className={classes.root}>
      <Box
        display="flex"
        flexDirection="column"
        zIndex={1}
        alignItems="stretch"
        height="100%"
        data-qa="course-card-grid-item"
      >
        <Box flex={1}>
          <CourseCardPicture
            imageSrc={imageSrc}
            hasNote={hasNote}
            isSelfAssigned={isSelfAssigned}
          />
          {courseDescription ? (
            <Tooltip
              interactive={false}
              title={
                <Typography variant="body2" color="textPrimary">
                  {courseDescription}
                </Typography>
              }
            >
              {courseCardTooltipContainer}
            </Tooltip>
          ) : (
            courseCardTooltipContainer
          )}
          {flags && (
            <Box display="flex" mt={2} mb={1} mx={3}>
              {flags}
            </Box>
          )}
        </Box>
        <Box display="flex" flexDirection="column" mb={3} mx={3}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            {(dueDate || location) && (
              <Box display="flex" flexDirection="column" mt={1}>
                {duration}
                {dueDate}
                {location}
              </Box>
            )}
            {reattemptMessage}
            <Box
              display="flex"
              flexDirection="column"
              mt={2}
              className={classes.buttons}
            >
              {buttons}
            </Box>
            {activitiesSummary}
            {expansionPanel}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default CourseCardGridType;

CourseCardGridType.propTypes = {
  buttons: PropTypes.node,
  expansionPanel: PropTypes.node,
  hasNote: PropTypes.bool,
  title: PropTypes.string,
  courseType: PropTypes.string,
  typeName: PropTypes.string,
  flags: PropTypes.node,
  imageSrc: PropTypes.string,
  activitiesSummary: PropTypes.node,
  dueDate: PropTypes.node,
  courseDescription: PropTypes.string,
  isSelfAssigned: PropTypes.bool
};
